import {
  ICompanyClient,
  ICompanyClientContact,
  ICompanyInvoice,
  ISingleClient,
  ISingleClientContact,
  ISingleCompany,
  ISingleDocument,
  IEmailTemplate,
  IEmailLog,
  ISingleProduct,
} from '@typings'
import { STORES } from './keys.contants'

const LOGIN = '/login'
const KVK = '/kvk'
const AUTH = '/auth'
const FINALISE = '/finalise'
const DOCUMENTS = '/documents'
const TOKEN = '/token'
const COMPANY = '/company'
const COMPANIES = '/companies'
const CONTACTS = '/contacts'
const CREATE = '/create'
const CREATE_EMPTY = '/create-empty'
const CLIENT = '/client'
const CLIENTS = '/clients'
const TOKEN_REFRESH = '/token-refresh'
const LOGOUT = '/logout'
const LOGO = '/logo'
const LOCATIONS = '/locations'
const REGISTER = '/register'
const USER = '/user'
const UPDATE = '/update'
const REMOVE = '/remove'
const WHO_AM_I = '/who-am-i'
const MY_COMPANY = '/my-company'
const VAT = '/vat'
const SHARED = '/shared'
const COUNTRIES = '/countries'
const DASHBOARD = '/dashboard'
const EMAIL_TEMPLATES = '/email-templates'
const EMAIL_LOGS = '/email-logs'
const SEND = '/send'
const PRODUCTS = '/products'
const OAUTH = '/oauth'
const GMAIL = '/gmail'
const MICROSOFT = '/microsoft'
const AUTHORIZE = '/authorize'
const STATUS = '/status'
const DISCONNECT = '/disconnect'

//Partials

const TASKS = '/tasks'
const BANK_ACCOUNT = '/bank-account'
const INVOICES = '/invoices'
const ALL = '/all'

export const NEW_ENDPOINTS = {
  AUTH: {
    LOGIN: `${AUTH}${LOGIN}`,
    FORGOT_PASSWORD: `${AUTH}/forgot-password`,
    RESET_PASSWORD: `${AUTH}/reset-password`,
    VERIFY_EMAIL: (id: string, hash: string) => `${AUTH}/verify/${id}/${hash}`,
    RESEND_VERIFICATION: `${AUTH}/resend-verification`,
  },
  DOCUMENTS: {
    LIST: `${DOCUMENTS}`,
    SINGLE: (ID: ISingleDocument['id']) => `${DOCUMENTS}/${ID}`,
    DOWNLOAD: (ID: ISingleDocument['id']) => `${DOCUMENTS}/${ID}/download`,
    PREVIEW: (ID: ISingleDocument['id']) => `${DOCUMENTS}/${ID}/preview`,
    DOWNLOAD_UBL: (ID: ISingleDocument['id']) => `${DOCUMENTS}/${ID}/download-ubl`,
    FINALISE: (ID: ISingleDocument['id']) => `${DOCUMENTS}/${ID}${FINALISE}`,
    BULK_DELETE: `${DOCUMENTS}/bulk-delete`,
    BULK_DOWNLOAD_ZIP: `${DOCUMENTS}/bulk-download-zip`,
  },
  CLIENT: {
    LIST: `${CLIENTS}`,
    SINGLE: (ID: ISingleClient['id']) => `${CLIENTS}/${ID}`,
    CONTACTS: {
      LIST: `${CLIENT}${CONTACTS}`,
      SINGLE: (ID: ISingleClientContact['id']) => `${CLIENT}${CONTACTS}/${ID}`,
    },
  },
  COMPANIES: {
    LIST: `${COMPANIES}`,
    SINGLE: (ID: ISingleCompany['id']) => `${COMPANIES}/${ID}`,
    LOGO: (ID: ISingleCompany['id']) => `${COMPANIES}/${ID}${LOGO}`,
  },
  SHARED: {
    VAT: `${SHARED}${VAT}`,
    COUNTRIES: `${SHARED}${COUNTRIES}`,
  },
  EMAIL: {
    TEMPLATES: {
      LIST: `${EMAIL_TEMPLATES}`,
      SINGLE: (ID: IEmailTemplate['id']) => `${EMAIL_TEMPLATES}/${ID}`,
      DEFAULT: (type: string) => `${EMAIL_TEMPLATES}/default/${type}`,
    },
    LOGS: {
      LIST: `${EMAIL_LOGS}`,
      SINGLE: (ID: IEmailLog['id']) => `${EMAIL_LOGS}/${ID}`,
    },
    SEND_DOCUMENT: (ID: ISingleDocument['id']) => `${DOCUMENTS}/${ID}${SEND}`,
  },
  PRODUCT: {
    LIST: `${PRODUCTS}`,
    SINGLE: (ID: ISingleProduct['id']) => `${PRODUCTS}/${ID}`,
  },
  OAUTH: {
    GMAIL: {
      AUTHORIZE: `${OAUTH}${GMAIL}${AUTHORIZE}`,
      STATUS: `${OAUTH}${GMAIL}${STATUS}`,
      DISCONNECT: `${OAUTH}${GMAIL}${DISCONNECT}`,
    },
    MICROSOFT: {
      REDIRECT: `${OAUTH}${MICROSOFT}/redirect`,
      STATUS: `${OAUTH}${MICROSOFT}${STATUS}`,
      DISCONNECT: `${OAUTH}${MICROSOFT}${DISCONNECT}`,
    },
  },
  DASHBOARD: `${DASHBOARD}`,
}

export const ENDPOINTS = {
  AUTH: {
    TOKEN: `${AUTH}${TOKEN}`,
    REFRESH_TOKEN: `${AUTH}${TOKEN_REFRESH}`,
    LOGOUT: `${AUTH}${LOGOUT}`,
    REGISTER: `${AUTH}${REGISTER}`,
  },
  COMPANY: {
    GET_MY_COMPANY: `${COMPANY}${MY_COMPANY}`,
    CREATE: `${COMPANIES}`,
    TASKS: {
      ALL: `${COMPANY}${TASKS}${ALL}`,
      CREATE: `${COMPANY}${TASKS}${CREATE}`,
    },
    BANK_ACCOUNT: {
      CREATE: `${COMPANY}${BANK_ACCOUNT}${CREATE}`,
      ALL: `${COMPANY}${BANK_ACCOUNT}${ALL}`,
    },
    INVOICES: {
      SINGLE: (id: ICompanyInvoice['id']) => `${COMPANY}${INVOICES}/${id}`,
      CREATE: `${COMPANY}${INVOICES}${CREATE}`,
      ADD_TASK: (id: ICompanyInvoice['id']) => `${COMPANY}${INVOICES}/${id}/task/add`,
      CREATE_EMPTY: `${COMPANY}${INVOICES}${CREATE_EMPTY}`,
      ALL: `${COMPANY}${INVOICES}${ALL}`,
      REMOVE: `${COMPANY}${INVOICES}${REMOVE}`,
    },
    CLIENTS: {
      CREATE: `${COMPANY}${CLIENTS}${CREATE}`,
      ALL: `${COMPANY}${CLIENTS}${ALL}`,
      CONTACTS: {
        ALL: (ID: ICompanyClient['id']) => `${COMPANY}${CLIENTS}/${ID}${CONTACTS}${ALL}`,
        CREATE: (ID: ICompanyClient['id']) => `${COMPANY}${CLIENTS}/${ID}${CONTACTS}${CREATE}`,
        SINGLE: (CLIENT_ID: ICompanyClient['id'], CONTACT_ID: ICompanyClientContact['id']) =>
          `${COMPANY}${CLIENTS}/${CLIENT_ID}${CONTACTS}/${CONTACT_ID}`,
      },
    },
    LOCATIONS: {
      CREATE: `${COMPANY}${LOCATIONS}${CREATE}`,
      ALL: `${COMPANY}${LOCATIONS}${ALL}`,
    },
  },
  USER: {
    WHO_AM_I: `${USER}${WHO_AM_I}`,
    UPDATE: `${USER}${UPDATE}`,
    CHANGE_PASSWORD: `${USER}/change-password`,
    REQUEST_EMAIL_CHANGE: `${USER}/request-email-change`,
    CANCEL_EMAIL_CHANGE: `${USER}/cancel-email-change`,
    RESEND_EMAIL_CHANGE: `${USER}/resend-email-change`,
  },
  KVK: `${KVK}`,
  KVK_SEARCH: `${KVK}/search`,
  VAT: {
    ALL: `${VAT}${ALL}`,
  },
}

export const DATAGRID_ENDPOINTS = {
  [STORES.INVOICE]: {
    GET: NEW_ENDPOINTS.DOCUMENTS.LIST,
    REMOVE: ENDPOINTS.COMPANY.INVOICES.REMOVE,
  },
  [STORES.QUOTATION]: {
    GET: NEW_ENDPOINTS.DOCUMENTS.LIST,
  },
  [STORES.CLIENT]: {
    GET: NEW_ENDPOINTS.CLIENT.LIST,
  },
}
